/**
 * Pieces both report sections use.
 *
 * Split out when Reports grew a tab strip: a headline figure, a labelled
 * breakdown table, the retainer sparkline, and the period pager that sits at
 * the top of each section. Nothing here knows which section it is in.
 */

import { useCallback, useState } from "react";
import { ProgressBar, card } from "@/components/atoms";
import { MonthPicker, QuarterPicker, WeekPicker, monthOffsetOf, quarterOffsetOf, weekOffsetOf } from "@/components/PeriodPicker";
import { hours, money, periodBounds, retainerFinding, retainerHistory, totalsOf, type Period, type PeriodBounds } from "@/lib/timeRollup";
import type { Project, TimeEntry } from "@/lib/types";

/**
 * Twelve months of a retainer, measured against a dashed allowance line, so
 * overage is literally the part of the bar above the line. Thresholds are the
 * ones the progress bar above already uses, so the two cannot disagree.
 */
export function RetainerStrip({ project, entries }: { project: Project; entries: TimeEntry[] }) {
  const months = retainerHistory(project, entries);
  if (months.length === 0) return null;

  // The plot has to fit the tallest bar AND the allowance line, or a month that
  // never reaches the allowance pushes the line off the top.
  const ceiling = Math.max(1, ...months.map((m) => m.ratio)) * 1.1;
  const ALLOWANCE_Y = (1 / ceiling) * 100;
  const H = 46;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ position: "relative", height: H, display: "flex", gap: 3, alignItems: "flex-end" }}>
        {/* The allowance line, dashed, drawn across every column. */}
        <div style={{
          position: "absolute", left: 0, right: 0, bottom: `${ALLOWANCE_Y}%`,
          borderTop: "1px dashed var(--line2)", pointerEvents: "none",
        }} />
        {months.map((m, i) => {
          const tone = m.ratio > 1 ? "var(--bad)" : m.ratio >= 0.85 ? "var(--warn)" : "var(--good)";
          return (
            <div key={i} style={{ flex: 1, height: "100%", display: "flex", alignItems: "flex-end" }}
                 title={m.beforeStart ? `${m.label}: before the engagement started` : `${m.label}: ${m.hours.toFixed(2)} h of ${project.retainerHours}`}>
              {m.beforeStart ? (
                // No data and no hours are different facts.
                <div style={{ width: "100%", borderTop: "1px solid var(--line2)" }} />
              ) : (
                <div style={{
                  width: "100%", background: tone, borderRadius: "2px 2px 0 0",
                  height: `${Math.max(m.ratio > 0 ? 2 : 0, Math.min(100, (m.ratio / ceiling) * 100))}%`,
                }} />
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 3, marginTop: 4 }}>
        {months.map((m, i) => (
          <span key={i} style={{ flex: 1, textAlign: "center", fontSize: 9.5, color: "var(--muted3)" }}>{m.label}</span>
        ))}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 5 }}>
        Last twelve months against the {project.retainerHours} h allowance — {retainerFinding(months)}
      </div>
    </div>
  );
}

export function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--muted2)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      {/* Deliberately NOT the mono face: these are headline figures, read at a
          glance rather than scanned in a column, and the monospace reads as
          code here. The aligned columns further down keep mono. */}
      <div style={{ fontSize: 22, fontWeight: 600, marginTop: 2, letterSpacing: "-0.02em" }}>{value}</div>
    </div>
  );
}

/**
 * A breakdown, in the tracker's Insights shape.
 *
 * Not a two-column table of label and figure. Each row is a colour dot, the
 * name, the hours at a size you can read across the room, the money quietly
 * beside it, and a share bar underneath — so "which of these is most of my
 * month" is answered by bar length before you read a single number.
 *
 * The dot takes the project's own colour where there is one, which makes a row
 * here and a segment in the chart above the same colour without a legend.
 */
export function Breakdown<T extends { label: string; totals: ReturnType<typeof totalsOf> }>({
  title, rows, hint, onRow, colorOf,
}: {
  title: string;
  rows: T[];
  hint?: string;
  /** When given, each row becomes a button — used to open what is behind it. */
  onRow?: (row: T) => void;
  colorOf?: (row: T) => string | undefined;
}) {
  const total = rows.reduce((n, r) => n + r.totals.seconds, 0);

  return (
    <div style={{ ...card, padding: 18 }}>
      <div style={{ fontSize: "var(--fs-panel)", fontWeight: "var(--fw-title)" }}>{title}</div>
      {hint && <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 3 }}>{hint}</div>}
      {rows.length === 0 && (
        <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 8 }}>Nothing in this period.</div>
      )}
      {rows.map((r) => {
        const share = total > 0 ? Math.round((r.totals.seconds / total) * 100) : 0;
        const color = colorOf?.(r) ?? "var(--accent)";
        const body = (
          <>
            <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span style={{ flex: "none", width: 8, height: 8, borderRadius: 2, background: color }} />
              <span style={{
                flex: 1, minWidth: 0, fontSize: 13.5, color: "var(--ink)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>{r.label}</span>
              <span className="mono" style={{ flex: "none", fontSize: 16, fontWeight: 600, color: "var(--ink)" }}>
                {hours(r.totals.seconds)}h
              </span>
              <span className="mono" style={{ flex: "none", minWidth: 62, textAlign: "right", fontSize: 12, color: "var(--muted)" }}>
                {r.totals.amount > 0 ? money(r.totals.amount, r.totals.currency) : "—"}
              </span>
            </span>
            <span style={{ display: "block", marginLeft: 17, height: 4, borderRadius: 999, background: "var(--tint2)", overflow: "hidden" }}>
              <span style={{ display: "block", height: "100%", width: `${share}%`, background: color, borderRadius: 999 }} />
            </span>
          </>
        );
        const rowStyle: React.CSSProperties = {
          display: "flex", flexDirection: "column", gap: 8, width: "100%",
          padding: "11px 0", borderTop: "1px solid var(--tint2)", textAlign: "left",
        };
        return onRow ? (
          <button
            key={r.label}
            onClick={() => onRow(r)}
            className="row-hover"
            style={{ ...rowStyle, border: "none", borderTop: "1px solid var(--tint2)", background: "none", color: "var(--ink)", cursor: "pointer" }}
          >{body}</button>
        ) : (
          <div key={r.label} style={rowStyle}>{body}</div>
        );
      })}
    </div>
  );
}

/**
 * Period chooser and pager.
 *
 * One component rather than a copy per section, so the two can never drift
 * into offering different periods or stepping by different amounts.
 */
export function PeriodPager({ periods, period, offset, bounds, onPeriod, onOffset, trailing, marked }: {
  periods: [Period, string][];
  period: Period;
  offset: number;
  bounds: PeriodBounds;
  onPeriod: (p: Period) => void;
  onOffset: (fn: (o: number) => number) => void;
  trailing?: React.ReactNode;
  /** Days with time logged (dayKey), dotted in the picker. */
  marked?: Set<string>;
}) {
  const [picking, setPicking] = useState(false);
  // A pick becomes the offset the ‹ › pager would have reached. Reports never
  // pages into the future, so neither does the picker.
  const pick = (to: number) => { onOffset(() => Math.min(0, to)); setPicking(false); };
  const close = useCallback(() => setPicking(false), []);
  const now = new Date();
  const picker =
    period === "week" ? <WeekPicker selected={bounds.start} marked={marked} latest={now} onClose={close} onPick={(d) => pick(weekOffsetOf(d))} />
    : period === "month" ? <MonthPicker selected={bounds.start} marked={marked} latest={now} onClose={close} onPick={(d) => pick(monthOffsetOf(d))} />
    : period === "quarter" ? <QuarterPicker selected={bounds.start} marked={marked} latest={now} onClose={close} onPick={(d) => pick(quarterOffsetOf(d))} />
    : null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
      <div style={{ display: "flex", padding: 3, background: "var(--tint)", borderRadius: 999 }}>
        {periods.map(([k, label]) => {
          const on = k === period;
          return (
            <button key={k} onClick={() => { onPeriod(k); onOffset(() => 0); setPicking(false); }} style={{
              padding: "6px 13px", borderRadius: 999, fontSize: 12.5, cursor: "pointer", border: "none",
              background: on ? "var(--surface)" : "transparent",
              color: on ? "var(--accent)" : "var(--ink3)",
              fontWeight: on ? "var(--fw-title)" : "var(--fw-medium)",
              boxShadow: on ? "var(--shadowCard)" : "none",
            }}>{label}</button>
          );
        })}
      </div>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button onClick={() => onOffset((o) => o - 1)} style={stepBtn} aria-label="Previous period">‹</button>
        <span style={{ position: "relative" }}>
          <button
            onClick={() => setPicking((v) => !v)}
            // The picker closes on any outside mousedown; without this, a click
            // here would close it and the click would reopen it.
            onMouseDown={(e) => e.stopPropagation()}
            disabled={!picker}
            aria-haspopup="dialog"
            aria-expanded={picking}
            title={picker ? `Pick a ${period}` : undefined}
            style={{
              fontSize: 13, color: "var(--ink2)", minWidth: 150, textAlign: "center", padding: "4px 10px",
              borderRadius: 8, border: "1px solid var(--line2)", background: "var(--surface)",
              cursor: picker ? "pointer" : "default", fontFamily: "inherit",
            }}
          >{bounds.label}</button>
          {picking && picker}
        </span>
        <button
          onClick={() => onOffset((o) => Math.min(0, o + 1))} disabled={bounds.isCurrent}
          aria-label="Next period"
          style={{ ...stepBtn, opacity: bounds.isCurrent ? 0.35 : 1, cursor: bounds.isCurrent ? "default" : "pointer" }}
        >›</button>
      </span>
      {trailing && <span style={{ marginLeft: "auto" }}>{trailing}</span>}
    </div>
  );
}

export const stepBtn: React.CSSProperties = {
  width: 26, height: 26, borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 14, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
};
export const ghostBtn: React.CSSProperties = {
  padding: "7px 14px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer",
};
